  import React, { useEffect, useState, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
  import { Canvas, useThree } from "@react-three/fiber";
  import { OrbitControls } from "@react-three/drei";
  import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
  import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
  import * as THREE from "three";
  import { toast } from "react-toastify";

  import "../../style/reuploadaddscan.css";
  import { apiAxios } from "../../utils/apiAxios";

  interface ExistingFile {
    name: string;
    url: string;
  }

  interface ModelViewerProps {
    files: (File | ExistingFile)[];
    onDelete: (index: number) => void;
  }

  interface FileObjectPair {
    file: File | ExistingFile;
    object: THREE.Object3D | null;
    isLoading: boolean;
    isImage: boolean;
    imageUrl?: string;
    visible: boolean;
  }

  const UPPER_COLOR = "#dbe6dcff";
  const LOWER_COLOR = "#dbe6dcff";

  const FitCamera: React.FC<{ objects: THREE.Object3D[] }> = ({ objects }) => {
    const { camera, controls } = useThree();

    useEffect(() => {
      if (!objects.length) return;

      const box = new THREE.Box3();
      objects.forEach((obj) => box.expandByObject(obj));

      const center = new THREE.Vector3();
      box.getCenter(center);

      const size = new THREE.Vector3();
      box.getSize(size);

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = (camera as THREE.PerspectiveCamera).fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

      camera.position.set(center.x, center.y, cameraZ * 1.5);
      camera.lookAt(center);
      (camera as THREE.PerspectiveCamera).updateProjectionMatrix();

      if (controls) {
        (controls as any).target.copy(center);
        (controls as any).update();
      }
    }, []);

    return null;
  };

  export interface ReuploadScanProps {
    onContinueClick?: () => void;
  }
  interface Props {
    caseID: number;
    currentOrderState?  :string
   
    onContinueClick: () => void;
    onBack: () => void;   // 👈 add this
  }

  const ReuploadAddScan: React.FC<Props> = ({ caseID,currentOrderState, onContinueClick, onBack }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileObjects, setFileObjects] = useState<FileObjectPair[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [hasUploadedFiles, setHasUploadedFiles] = useState<boolean>(false);
    const [files, setFiles] = useState<(File | ExistingFile)[]>([]);
    const [isFileSelected, setIsFileSelected] = useState(false);

    useEffect(() => {
      window.scrollTo({ top: 0 });
    }, []);

    const validFiles = useMemo(() => {
      return files.filter(file => {
        const fileName = file instanceof File ? file.name : file.name;
        const ext = fileName.toLowerCase().split('.').pop();
        return ext === 'stl' || ext === 'obj';
      });
    }, [files]);

    const handleReuploadSubmit = async () => {
      if (!caseID) {
        toast.error("Case ID missing");
        return;
      }

      if (validFiles.length === 0) {
        toast.warning("Please upload at least one scan file");
        return;
      }

      try {
        setIsSubmitting(true);

        const formData = new FormData();
        formData.append("CaseID", caseID.toString());

        validFiles.forEach((file) => {
          if (file instanceof File) {
            formData.append("Files", file);
          }
        });

        const token = sessionStorage.getItem("token");

        // const { data } = await apiAxios.post(
        //   `/Dentist/ReuploadCaseFiles`,
        //   formData,
        //   {
        //     headers: {
        //       AuthorizationData: token || "",
        //     },
        //   }
        // );

        const apiUrl =
  currentOrderState === "WARRANTY_CLAIMED"
    ? "/Dentist/ReuploadWrrantyCaseFiles"
    : "/Dentist/ReuploadCaseFiles";

const { data } = await apiAxios.post(
  apiUrl,
  formData,
  {
    headers: {
      AuthorizationData: token || "",
    },
  }
);

        if (data?.isSuccess) {
          toast.success("Scan reuploaded successfully");
          onContinueClick?.();
        } else {
          toast.error(data?.status || "Failed to reupload scan");
        }
      } catch (error: any) {
        console.error("Reupload error:", error);

        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while uploading";

        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    };

    useEffect(() => {
      if (files.length > 0 && validFiles.length === 0) {
        setErrorMessage("Please upload only STL or OBJ files. Other file types are not supported.");
      } else {
        setErrorMessage("");
      }

      if (validFiles.length > 0) {
        setHasUploadedFiles(true);
      } else {
        setHasUploadedFiles(false);
      }
    }, [files, validFiles]);

    useEffect(() => {
      if (!validFiles || validFiles.length === 0) {
        setFileObjects([]);
        return;
      }

      const initialFileObjects: FileObjectPair[] = validFiles.map((file) => ({
        file,
        object: null,
        isLoading: true,
        isImage: false,
        visible: true,
      }));

      setFileObjects(initialFileObjects);

      const loaders = {
        stl: new STLLoader(),
        obj: new OBJLoader(),
      };

      const load3DModel = (
        data: ArrayBuffer | string,
        fileName: string,
        idx: number
      ) => {
        let object: THREE.Object3D | null = null;
        try {
          if (fileName.toLowerCase().endsWith(".stl") && data instanceof ArrayBuffer) {
            const geometry = loaders.stl.parse(data);
            geometry.computeVertexNormals();
            geometry.computeBoundingBox();
            const box = geometry.boundingBox!;
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            const scale = 5 / Math.max(size.x, size.y, size.z);

            geometry.translate(-center.x, -center.y, -center.z);
            geometry.scale(scale, scale, scale);

            object = new THREE.Mesh(
              geometry,
              new THREE.MeshStandardMaterial({ color: "#e0e0e0" })
            );
            object.rotation.x = -Math.PI / 2;
            object.rotation.z = Math.PI;
            (object as any).fileName = fileName;

          } else if (fileName.toLowerCase().endsWith(".obj") && typeof data === "string") {
            object = loaders.obj.parse(data);

            object.traverse((child: any) => {
              if (child.isMesh)
                child.material = new THREE.MeshStandardMaterial({
                  color: "#e0e0e0",
                });
            });

            const box = new THREE.Box3().setFromObject(object);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            const scale = 5 / Math.max(size.x, size.y, size.z);

            object.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
            object.scale.set(scale, scale, scale);

            (object as any).fileName = fileName;
          }
        } catch (err) {
          console.error("Error parsing file:", err);

          let errorMessage = "Unknown error occurred";
          if (err instanceof Error) {
            errorMessage = err.message;
          } else if (typeof err === "string") {
            errorMessage = err;
          }
          setErrorMessage(`Error loading file ${fileName}: ${errorMessage}`);
        }

        setFileObjects((prev) =>
          prev.map((f, i) =>
            i === idx ? { ...f, object, isLoading: false } : f
          )
        );
      };

      validFiles.forEach((file, idx) => {
        const fileName = file instanceof File ? file.name : file.name;

        if (file instanceof File) {
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target?.result) {
              load3DModel(e.target.result, fileName, idx);
            }
          };

          if (fileName.toLowerCase().endsWith(".stl"))
            reader.readAsArrayBuffer(file);
          else
            reader.readAsText(file);
        }
      });

    }, [validFiles]);

    useEffect(() => {
      const loadedObjects = fileObjects.filter(f => f.object && !f.isLoading);

      if (loadedObjects.length === 1) {
        const obj = loadedObjects[0].object!;
        const fileName = (obj as any).fileName?.toLowerCase() || '';

        if (fileName.includes('upper')) {
          obj.position.y = 1.0;
          obj.traverse((child: any) => {
            if (child.isMesh) child.material.color.set(UPPER_COLOR);
          });
        } else if (fileName.includes('lower')) {
          obj.position.y = -1.0;
          obj.traverse((child: any) => {
            if (child.isMesh) child.material.color.set(LOWER_COLOR);
          });
        }

      } else if (loadedObjects.length === 2) {
        const [obj1, obj2] = loadedObjects.map(f => f.object!);
        const fileName1 = (obj1 as any).fileName?.toLowerCase() || '';
        const fileName2 = (obj2 as any).fileName?.toLowerCase() || '';

        if (fileName1.includes('upper') || !fileName2.includes('upper')) {
          obj1.position.y = 0.35;
          obj2.position.y = -0.35;

          obj1.traverse((child: any) => {
            if (child.isMesh) child.material.color.set(UPPER_COLOR);
          });
          obj2.traverse((child: any) => {
            if (child.isMesh) child.material.color.set(LOWER_COLOR);
          });
        } else {
          obj2.position.y = 0.36;
          obj1.position.y = -0.36;

          obj2.traverse((child: any) => {
            if (child.isMesh) child.material.color.set(UPPER_COLOR);
          });
          obj1.traverse((child: any) => {
            if (child.isMesh) child.material.color.set(LOWER_COLOR);
          });
        }
      }
    }, [fileObjects]);

    const toggleVisibility = (index: number) => {
      setFileObjects(prev =>
        prev.map((f, i) =>
          i === index ? { ...f, visible: !f.visible } : f
        )
      );
    };
    const [gapOffset, setGapOffset] = useState<number>(0);

    const visibleObjects = useMemo(() => {
      return fileObjects
        .filter(f => f.visible && f.object && !f.isLoading)
        .map(f => f.object!) as THREE.Object3D[];
    }, [fileObjects]);
    useEffect(() => {
      const loaded = fileObjects.filter(f => f.object && !f.isLoading);

      if (loaded.length === 0) return;

      // Separate upper and lower models
      const upperModels = loaded.filter(fileObj => {
        const fileName = ((fileObj.object as any).fileName || '').toLowerCase();
        return fileName.includes("upper") || fileName.includes("maxillary");
      });

      const lowerModels = loaded.filter(fileObj => {
        const fileName = ((fileObj.object as any).fileName || '').toLowerCase();
        return fileName.includes("lower") || fileName.includes("mandibular");
      });

      // Calculate bounding boxes for each model
      const getModelBounds = (obj: THREE.Object3D) => {
        const box = new THREE.Box3().setFromObject(obj);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        return { box, size, center };
      };

      // Dynamic gap calculation
      let baseGap = 0;

      if (upperModels.length > 0 && lowerModels.length > 0) {
        const upperHeights = upperModels.map(f => getModelBounds(f.object!).size.y);
        const lowerHeights = lowerModels.map(f => getModelBounds(f.object!).size.y);

        const maxUpperHeight = Math.max(...upperHeights);
        const maxLowerHeight = Math.max(...lowerHeights);

        baseGap = (maxUpperHeight + maxLowerHeight) * 0.2;
      }
      else if (upperModels.length > 0 || lowerModels.length > 0) {
        const singleModel = upperModels.length > 0 ? upperModels[0] : lowerModels[0];
        const { size } = getModelBounds(singleModel.object!);
        baseGap = size.y * 0.4;
      }

      // Final gap = baseGap + manual offset
      const gap = Math.max(0, baseGap + gapOffset);


      // Position upper models
      upperModels.forEach((fileObj) => {
        const obj = fileObj.object!;
        obj.rotation.set(0, 0, 0);
        obj.position.y = gap / 2;
        obj.traverse((child: any) => {
          if (child.isMesh)
            child.material.color.set(UPPER_COLOR);
        });
      });

      // Position lower models
      lowerModels.forEach((fileObj) => {
        const obj = fileObj.object!;
        obj.rotation.set(0, 0, 0);
        obj.position.y = -gap / 2;
        obj.traverse((child: any) => {
          if (child.isMesh)
            child.material.color.set(LOWER_COLOR);
        });
      });

    }, [fileObjects, gapOffset]);
    const [isDragging, setIsDragging] = useState(false);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList) return;

      const newFiles = Array.from(fileList);

      const validFiles = newFiles.filter(file => {
        const ext = file.name.toLowerCase().split('.').pop();
        return ext === 'stl' || ext === 'obj';
      });

      if (validFiles.length !== newFiles.length) {
        toast.warning("Only STL and OBJ files are allowed.");
      }

      setFiles(prev => [...prev, ...validFiles]);
      setIsFileSelected(true);
    };

    const deleteFile = (index: number) => {
      setFiles(prev => prev.filter((_, i) => i !== index));
      if (index === 0) {
        setIsFileSelected(false);
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      const newFiles = Array.from(droppedFiles);

      const validFiles = newFiles.filter(file => {
        const ext = file.name.toLowerCase().split(".").pop();
        return ext === "stl" || ext === "obj";
      });

      if (validFiles.length !== newFiles.length) {
        toast.warning("Only STL and OBJ files are allowed.");
      }

      setFiles(prev => [...prev, ...validFiles]);
      setIsFileSelected(true);
    };


    return (

      <div className="case-details2 order-wrapper mt-3">
        <div className="d-flex">

          {/* <span className="back-text-btn"><i  className="bi bi-arrow-left-short" ></i> Back</span> */}
          <span
            className="back-text-btn"
            style={{ cursor: "pointer" }}
            onClick={onBack}
          >
            <i className="bi bi-arrow-left-short"></i> Back
          </span>

          <div className="please-upload">Please upload a new scan</div>

        </div>

        <div className="tab-content whole-tab-content mt-3 pt-2">
          <div className="tab-pane fade show active">
            <div className="row">
              <div className="col-md-12">
                {!isFileSelected && (

                  <input
                    id="fileUpload"
                    type="file"
                    multiple
                    accept=".stl,.obj"
                    onChange={handleFileInput}
                    hidden
                  />
                )}
              </div>
            </div>
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              style={{ width: "100%" }}
            >
              <div className="viewer-container-top">
                <div className="viewer-container">
                  {errorMessage && (
                    <div className="error-message">
                      {errorMessage}
                    </div>
                  )}


                  <div className="row mt-1">
                    <div className="col-md-12">
                      <div className={`viewer-canvas ${isDragging ? "drag-active" : ""}`}>
                        {hasUploadedFiles && (
                          <ul className="btn-plus-and-minus">
                            <li>
                              <div className="gap-label">Adjust Bite Gap</div>
                            </li>
                            <li>
                              <div className="gap-buttons">
                                <button
                                  onClick={() => setGapOffset(prev => prev - 0.1)}
                                  className="gap-btn"
                                >
                                  -
                                </button>

                                <span className="gap-value">
                                  {gapOffset.toFixed(2)}
                                </span>

                                <button
                                  onClick={() => setGapOffset(prev => prev + 0.1)}
                                  className="gap-btn"
                                >
                                  +
                                </button>
                              </div>
                            </li>
                          </ul>
                        )}
                        <Canvas
                          camera={{ position: [1, 1, 1], fov: 50 }}
                          style={{ background: hasUploadedFiles ? '#000000' : '#F8F9FB' }}
                        >
                          <color attach="background" args={[hasUploadedFiles ? '#000000' : '#F8F9FB']} />

                          <ambientLight intensity={hasUploadedFiles ? 0.8 : 0.6} />
                          <directionalLight position={[10, 10, 5]} intensity={hasUploadedFiles ? 1.2 : 1} />
                          <pointLight position={[-10, -10, -5]} intensity={0.3} />
                          <OrbitControls enableDamping dampingFactor={0.05} minDistance={0} maxDistance={50} />
                          {visibleObjects.length > 0 && <FitCamera objects={visibleObjects} />}

                          {fileObjects.map((fileObj, idx) => {
                            if (!fileObj.visible || !fileObj.object) return null;
                            return <primitive key={`object-${idx}`} object={fileObj.object} />;
                          })}

                        </Canvas>


                        {validFiles.length === 0 && !errorMessage && (
                          <div className="empty-preview">
                            <p>Drag & drop</p>
                            <p>or</p>
                            <div className="file-input-section-whichwillgoinside file-input-section-whichwillgoinside-reupload">
                              <div className="customfileupload">
                                <label htmlFor="fileUpload" className="upload-btn">
                                  <img alt="Logo" src="../flaticon/AddScan.png" ></img>
                                  <span className="upload-scan-text">Add Scan (s)</span><br />
                                </label>

                              </div></div>
                            <p className="upload-scan-text"></p>

                            <p className="st-and-objfiles">(.stl & .obj files only)</p>
                          </div>
                        )}
                      </div>
                      <div className="row ">
                        {/* {!hasUploadedFiles && ( */}
                          <div className="row mx-0 p-0">
                            <div className="col-md-12 mt-3">
                              <div className="share-direcly">
                                <input className="option-checkbox" type="checkbox" />
                                <div className="share-text2">
                                  Shared Directly with us? Add note with the file name/link below.
                                </div>
                              </div>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Scanner Name/File URL"
                              />
                            </div>
                          </div>
                        {/* )} */}

                      </div>
                    </div>

                    <div className="col-md-12 ">
                      <div className="viewer-sidebar-reupload ">
                        {fileObjects.length > 0 && (
                          <div className="file-card-group">
                            <div className="file-header">
                              <span className="file-title">
                                {fileObjects[0].file instanceof File
                                  ? fileObjects[0].file.name
                                  : fileObjects[0].file.name}
                              </span>
                              <button
                                className="delete-button"
                                onClick={() => {
                                  const indicesToDelete: number[] = [];
                                  fileObjects.forEach((fileObj, index) => {
                                    if (fileObj.visible) {
                                      indicesToDelete.push(index);
                                    }
                                  });

                                  for (let i = indicesToDelete.length - 1; i >= 0; i--) {
                                    deleteFile(indicesToDelete[i]);
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </div>

                            <div className="file-options">
                              {fileObjects.map((fileObj, index) => {
                                const fileName = fileObj.file instanceof File ? fileObj.file.name : fileObj.file.name;
                                const isUpper = fileName.toLowerCase().includes('upper');
                                const isLower = fileName.toLowerCase().includes('lower');

                                return (
                                  <div key={index} className="option-row input-checkoption-row">
                                    <span className="option-label">
                                      {isUpper ? 'Upper' : isLower ? 'Lower' : fileName}
                                    </span>
                                    <input
                                      type="checkbox"
                                      checked={fileObj.visible}
                                      onChange={() => toggleVisibility(index)}
                                      className="option-checkbox"
                                    />
                                  </div>
                                );
                              })}
                            </div>

                            {fileObjects.some(f => f.isLoading) && (
                              <div className="loading-indicator">Loading 3D models...</div>
                            )}
                          </div>
                        )}

                        {/* Info Box */}

                        <div className="list and-icon-list list-and-icon-list2">
                          <ul className="info-list">
                            <li className="number-fontweight re-upload-scan">  Please ensure:</li>
                            <li>Preview before submitting</li>
                            <li>Ensure Clear margins & full prep visible</li>
                            <li>Accurate bite alignment</li>
                            <li>No missing or distorted areas</li>
                            <li>Use supported file formats (.stl, .obj)</li>
                          </ul>
                        </div>


                        {/* Continue Button */}
                        <div className="sidebar-footer2-btn mt-3">
                          <button
                            className={`continue-btn-reupload ${validFiles.length === 0 || isSubmitting ? "btn-disabled" : ""
                              }`}
                            onClick={handleReuploadSubmit}
                            disabled={validFiles.length === 0 || isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Uploading...
                              </>
                            ) : validFiles.length === 0 ? "Submit" : "Continue"}
                          </button>


                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>
    );
  };

  export default ReuploadAddScan; 